import React, { useEffect, useState } from "react";

import {
  MapContainer,
  TileLayer,
  Polygon,
  Marker,
  Tooltip,
} from "react-leaflet";
import styled from "styled-components";
import variables from "../../../constants/assets/variables";
import "leaflet/dist/leaflet.css";
import * as turf from "@turf/turf";
import icon from "leaflet/dist/images/marker-icon.png";
import iconShadow from "leaflet/dist/images/marker-shadow.png";
import L from "leaflet";

let DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
});

const customMarker = new L.icon({
  iconUrl: "https://unpkg.com/leaflet@1.5.1/dist/images/marker-icon.png",
  iconSize: [15, 20],
  iconAnchor: [12, 15],
  popupAnchor: [0, 0],
});

const zoneMarker = new L.icon({
  iconUrl: "/images/zone-marker.png",
  iconSize: [20, 20],
  iconAnchor: [12, 15],
  popupAnchor: [0, 0],
});

L.Marker.prototype.options.icon = DefaultIcon;
const mapLocalitiesListToPolygon = (localitiesList) => {
  var features = [];
  for (const locality of localitiesList) {
    if (locality.boundary) {
      var new_polygon = turf.polygon([locality.boundary], {
        locality_id: locality.id,
        city_id: locality.city_id,
        city_name: locality.city_name,
        locality_name: locality.locality_name,
        id: locality.id,
        locality_supported: locality.locality_supported,
      });
      features = [...features, new_polygon];
    }
  }
  return features;
};

const mapZonesListToPolygon = (zonesList) => {
  var features = [];
  for (const zone of zonesList) {
    if (zone.boundary) {
      var new_polygon = turf.polygon([zone.boundary], {
        city_id: zone.city_id,
        city_name: zone.city_name,
        zone_name: zone.zone_name,
        id: zone.id,
        zone_supported: zone.zone_supported,
      });
      features = [...features, new_polygon];
    }
  }
  return features;
};

const mapsTileBaseUrl = "https://maps.zomato.com/osm";

const dissolveEntitiesInCites = (featurePolygons, property_name) => {
  var features = turf.featureCollection(featurePolygons);
  var dissolved_features = turf.dissolve(features, {
    propertyName: property_name,
  }).features;
  var temp_obj = {};
  for (const feature of dissolved_features) {
    if (!temp_obj[feature.properties.city_id]) {
      temp_obj[feature.properties.city_id] = [];
    }
    temp_obj[feature.properties.city_id] = [
      ...temp_obj[feature.properties.city_id],
      feature.geometry.coordinates,
    ];
  }
  var compressed_features = [];
  for (const city_boundary of Object.keys(temp_obj)) {
    compressed_features = [...compressed_features, temp_obj[city_boundary]];
  }
  return compressed_features;
};

const cityIntersections = (featurePolygons) => {
  var result = featurePolygons.flatMap((v, i) =>
    featurePolygons.slice(i + 1).map((w) => {
      return turf.intersect(turf.multiPolygon(v), turf.multiPolygon(w));
    })
  );
  return result;
};

function getDarkColor() {
  var colors = [
    variables.green500,
    variables.yellow500,
    variables.indigo500,
    variables.brown500,
    variables.purple500,
    variables.lime500,
    variables.blue500,
    variables.teal500,
    variables.orange500,
    variables.pink500,
    variables.cider500,
  ];
  return colors[Math.floor(Math.random() * colors.length)];
}

//show zones with dark colors
function getZoneColors() {
  var colors = [
    variables.black,
    variables.black900,
    variables.grey900,
    variables.grey800,
    variables.grey700,
    variables.grey600,
    variables.black700,
    variables.black800,
    variables.black600,
  ];
  return colors[Math.floor(Math.random() * colors.length)];
}

const warningPathOptions = {
  fillColor: variables.primaryColor,
  fill: true,
  fillOpacity: 0.8,
  stroke: false,
};

const zoneWarningPathOptions = {
  fillColor: variables.red900,
  fill: true,
  fillOpacity: 0.8,
  stroke: false,
};

const koramangala_coordinates = [12.9575, 77.6165];

const BoundaryMapViewer = ({ localitiesList, zonesList }) => {
  const [mapCentre, setMapCentre] = useState(koramangala_coordinates);
  const [intersectingPolygons, setIntersectingPolygons] = useState([]);
  const [intersectingZonePolygons, setIntersectingZonePolygons] = useState([]);
  const [dissolvedLocalities, setDissolvedLocalities] = useState([]);
  const [dissolvedZones, setDissolvedZones] = useState([]);
  const [selectedColours, setSelectedColours] = useState([]);
  const [selectedZoneColours, setSelectedZoneColours] = useState([]);
  const [localitiesPolygon, setLocalitiesPolygon] = useState([]);
  const [zonesPolygon, setZonesPolygon] = useState([]);
  const [
    inActiveLocalitiesDissolvedPolygons,
    setInActiveLocalitiesDissolvedPolygons,
  ] = useState([]);
  const [
    activeLocalitiesDissolvedPolygons,
    setActiveLocalitiesDissolvedPolygons,
  ] = useState([]);
  const [inActiveZonesDissolvedPolygons, setInActiveZonesDissolvedPolygons] =
    useState([]);
  const [activeZonesDissolvedPolygons, setActiveZonesDissolvedPolygons] =
    useState([]);

  const getIntersectionArea = (intersections, i, type) => {
    if (intersections) {
      return (
        <div>
          <Polygon
            stroke={false}
            key={i}
            positions={intersections.geometry.coordinates}
            pathOptions={
              type === "zone" ? zoneWarningPathOptions : warningPathOptions
            }
          ></Polygon>
          {intersections.geometry.type === "MultiPolygon" ? (
            intersections.geometry.coordinates.map((cc, j) => {
              let p = turf.pointOnFeature(turf.polygon(cc));
              return (
                <Marker position={p.geometry.coordinates} key={j}>
                  <Tooltip permanent>
                    <span>Overlapping area !</span>
                  </Tooltip>
                </Marker>
              );
            })
          ) : (
            <Marker
              position={
                turf.pointOnFeature(
                  turf.polygon(intersections.geometry.coordinates)
                ).geometry.coordinates
              }
            >
              <Tooltip permanent>
                <span>Overlapping area !</span>
              </Tooltip>
            </Marker>
          )}
        </div>
      );
    }
  };

  useEffect(() => {
    const polygons = mapLocalitiesListToPolygon(localitiesList);
    setLocalitiesPolygon(polygons);

    if (polygons.length > 0) {
      setMapCentre(turf.pointOnFeature(polygons[0]).geometry.coordinates);
    }

    const dissolvedPolygons = dissolveEntitiesInCites(polygons, "city_id");
    setDissolvedLocalities(dissolvedPolygons);
    setIntersectingPolygons(cityIntersections(dissolvedPolygons));

    const inactive_localities = localitiesList.filter(
      (locality) => !locality.locality_supported
    );
    const inactive_localities_polygon =
      mapLocalitiesListToPolygon(inactive_localities);
    const inActiveDissolvedPolygons = dissolveEntitiesInCites(
      inactive_localities_polygon,
      "city_id"
    );
    setInActiveLocalitiesDissolvedPolygons(inActiveDissolvedPolygons);

    const active_localities = localitiesList.filter(
      (locality) => locality.locality_supported
    );
    const active_localities_polygon =
      mapLocalitiesListToPolygon(active_localities);
    const activeDissolvedPolygons = dissolveEntitiesInCites(
      active_localities_polygon,
      "city_id"
    );
    setActiveLocalitiesDissolvedPolygons(activeDissolvedPolygons);
  }, [localitiesList]);

  useEffect(() => {
    //set zones
    const zonepolygons = mapZonesListToPolygon(zonesList);
    setZonesPolygon(zonepolygons);

    //if both zone and locality is available, then set center as zone center
    if (zonesPolygon.length > 0) {
      setMapCentre(turf.pointOnFeature(zonesPolygon[0]).geometry.coordinates);
    }

    const dissolvedZonesPolygons = dissolveEntitiesInCites(
      zonesPolygon,
      "city_id"
    );
    setDissolvedZones(dissolvedZonesPolygons);
    setIntersectingZonePolygons(cityIntersections(dissolvedZonesPolygons));

    const inactive_zones = zonesList.filter((zone) => !zone.zone_supported);
    const inactive_zones_polygon = mapZonesListToPolygon(inactive_zones);
    const inActiveDissolvedZonePolygons = dissolveEntitiesInCites(
      inactive_zones_polygon,
      "city_id"
    );
    setInActiveZonesDissolvedPolygons(inActiveDissolvedZonePolygons);

    const active_zones = zonesList.filter((zone) => zone.zone_supported);
    const active_zone_polygon = mapZonesListToPolygon(active_zones);
    const activeDissolvedZonePolygons = dissolveEntitiesInCites(
      active_zone_polygon,
      "city_id"
    );
    setActiveZonesDissolvedPolygons(activeDissolvedZonePolygons);
  }, [zonesList]);

  useEffect(() => {
    var newColours = new Array(dissolvedLocalities.length),
      colorMappings = {},
      retryLimit = 5;
    for (let i = 0; i < dissolvedLocalities.length; i++) {
      let randColor;
      for (let j = 0; j < retryLimit; j++) {
        randColor = getDarkColor();
        if (!(colorMappings[randColor] === true)) {
          break;
        }
      }
      newColours[i] = randColor;
      colorMappings[randColor] = true;
    }
    setSelectedColours(newColours);
  }, [dissolvedLocalities]);

  useEffect(() => {
    var newColours = new Array(dissolvedZones.length),
      colorMappings = {},
      retryLimit = 5;
    for (let i = 0; i < dissolvedZones.length; i++) {
      let randColor;
      for (let j = 0; j < retryLimit; j++) {
        randColor = getZoneColors();
        if (!(colorMappings[randColor] === true)) {
          break;
        }
      }
      newColours[i] = randColor;
      colorMappings[randColor] = true;
    }
    setSelectedZoneColours(newColours);
  }, [dissolvedZones]);

  return (
    <MapContainerWrapper>
      <MapContainer
        center={mapCentre}
        key={mapCentre[0] + mapCentre[1]}
        zoom={15}
        scrollWheelZoom={true}
        style={{ height: "100%", width: "100%", zIndex: 1 }}
      >
        {zonesPolygon.map((zone_polygon, i) => {
          let p = turf.center(zone_polygon);
          return (
            <div>
              <Marker icon={zoneMarker} position={p.geometry.coordinates}>
                <Tooltip>
                  <strong>Zone Name:</strong>{" "}
                  {zone_polygon.properties.zone_name}
                  <br />
                  <strong>City Name:</strong>{" "}
                  {zone_polygon.properties.city_name}
                  <br />
                  <strong>Zone ID:</strong> {zone_polygon.properties.id}
                  <br />
                  <strong>City ID:</strong> {zone_polygon.properties.city_id}
                  <br />
                  <strong>Zone Supported:</strong>{" "}
                  {zone_polygon.properties.zone_supported ? "True" : "False"}
                  <br />
                </Tooltip>
              </Marker>
              <Polygon
                key={i}
                positions={zone_polygon.geometry.coordinates}
                pathOptions={{
                  weight: 1,
                  color: "grey",
                  fillOpacity: 0.2,
                }}
              />
            </div>
          );
        })}
        {localitiesPolygon.map((locality_polygon, i) => {
          let p = turf.center(locality_polygon);
          return (
            <div>
              <Marker icon={customMarker} position={p.geometry.coordinates}>
                <Tooltip>
                  <strong>Locality Name:</strong>{" "}
                  {locality_polygon.properties.locality_name}
                  <br />
                  <strong>City Name:</strong>{" "}
                  {locality_polygon.properties.city_name}
                  <br />
                  <strong>Locality ID:</strong> {locality_polygon.properties.id}
                  <br />
                  <strong>City ID:</strong>{" "}
                  {locality_polygon.properties.city_id}
                  <br />
                  <strong>Locality Supported:</strong>{" "}
                  {locality_polygon.properties.locality_supported
                    ? "True"
                    : "False"}
                  <br />
                </Tooltip>
              </Marker>
              <Polygon
                key={i}
                positions={locality_polygon.geometry.coordinates}
                pathOptions={{
                  weight: 1,
                  color: "black",
                }}
              />
            </div>
          );
        })}
        {activeZonesDissolvedPolygons.map((city_cluster, i) => {
          return (
            <Polygon
              stroke={false}
              key={i}
              positions={city_cluster}
              pathOptions={{
                color:
                  selectedZoneColours.length >= i
                    ? selectedZoneColours[i]
                    : getZoneColors(),
                fillOpacity: 0.2,
              }}
            />
          );
        })}
        {inActiveZonesDissolvedPolygons.map((city_cluster, i) => {
          return (
            <Polygon
              stroke={false}
              key={i}
              positions={city_cluster}
              pathOptions={{
                color: variables.grey300,
                fillOpacity: 0.2,
              }}
            />
          );
        })}
        {activeLocalitiesDissolvedPolygons.map((city_cluster, i) => {
          return (
            <Polygon
              stroke={false}
              key={i}
              positions={city_cluster}
              pathOptions={{
                color:
                  selectedColours.length >= i
                    ? selectedColours[i]
                    : getDarkColor(),
                fillOpacity: 0.4,
              }}
            />
          );
        })}
        {inActiveLocalitiesDissolvedPolygons.map((city_cluster, i) => {
          return (
            <Polygon
              stroke={false}
              key={i}
              positions={city_cluster}
              pathOptions={{
                color: variables.grey500,
                fillOpacity: 0.4,
              }}
            />
          );
        })}
        {intersectingZonePolygons.map((intersections, i) => {
          return getIntersectionArea(intersections, i, "zone");
        })}
        {intersectingPolygons.map((intersections, i) => {
          return getIntersectionArea(intersections, i, "locality");
        })}
        <TileLayer url={mapsTileBaseUrl + "/{z}/{x}/{y}.png"} />
      </MapContainer>
    </MapContainerWrapper>
  );
};

const MapContainerWrapper = styled.div`
  background: ${variables.white};
  border: 2.5px solid ${variables.black};
  box-sizing: border-box;
  border-radius: 8px;
  height: 100%;
  width: 100%;
`;

export default BoundaryMapViewer;
